import React, { useRef, useState } from "react";
import { Archive, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { ticketsAPI } from "../services/api";
import { useToast } from "../hooks/use-toast";
import { getClosureToast, getTicketErrorMessage } from "../utils/ticketErrors";

const hint = "Você não assumirá o atendimento, apenas encerrará este contato. Nenhuma avaliação será solicitada.";

export default function ClosePotentialTicketButton({ ticket, onClosed, onClosingChange, className, disabled = false }) {
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const closingRef = useRef(false);
  const { toast } = useToast();

  const handleClose = async () => {
    if (closingRef.current) return;
    closingRef.current = true;
    setClosing(true);
    onClosingChange?.(true);
    try {
      const response = await ticketsAPI.closePotential(ticket.id);
      toast(getClosureToast(response.data, true));
      setOpen(false);
      onClosed?.(response.data);
    } catch (error) {
      toast({
        title: "Encerramento não confirmado",
        description: getTicketErrorMessage(error, "encerrar este contato"),
        variant: "destructive", duration: 10000,
      });
    } finally {
      closingRef.current = false;
      setClosing(false);
      onClosingChange?.(false);
    }
  };

  return (
    <>
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button type="button" variant="outline" title={hint}
              className={className} disabled={disabled || closing}
              onClick={() => setOpen(true)} data-testid={`close-potential-${ticket.id}`}>
              <Archive className="w-4 h-4 mr-2 shrink-0" />
              Encerrar atendimento
            </Button>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">{hint}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <Dialog open={open} onOpenChange={(next) => { if (!closingRef.current) setOpen(next); }}>
        <DialogContent className="w-full max-w-[95vw] sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Encerrar atendimento potencial</DialogTitle>
            <DialogDescription>
              Encerrar o contato de {ticket.client_name || "este cliente"}, protocolo {ticket.protocol}?
              {" "}{hint}
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            O cliente receberá uma mensagem informando o encerramento e poderá enviar uma nova mensagem caso precise de ajuda.
          </p>
          <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-2">
            <Button variant="outline" disabled={closing} onClick={() => setOpen(false)}>Cancelar</Button>
            <Button disabled={closing} onClick={handleClose} className="bg-orange-600 hover:bg-orange-700">
              {closing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {closing ? "Encerrando..." : "Confirmar encerramento"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
